'use client';

import type { ReactNode } from 'react';

import { useAtomInitialValues } from '@effect/atom-react';
import { Option } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';

import { fetchGlossaryTermsAtom } from '../../../../effect/atom/glossary/fetchGlossaryTerms';
import {
  alphabetFilterSearchParamAtom,
  nameFilterSearchParamAtom,
} from '../../../../effect/atom/glossary/glossaryFilters';
import { getGlossaryAlphabetAtom } from '../../../../effect/atom/glossary/getGlossaryAlphabet';
import type { GlossaryAlphabet, GlossaryTermIndex } from '@vs-shop/schemas/branded/MiscSchema';
import type { HydraCollectionResponseType } from '@vs-shop/schemas/openapi/response';

type Props = {
  alphabetFilter: string | null;
  nameFilter: string | null;
  onlyFavorites: boolean;
  page: number;
  itemsPerPage: number;
  termsResponse: HydraCollectionResponseType<typeof GlossaryTermIndex>;
  alphabetResponse: GlossaryAlphabet;
  children: ReactNode;
};

export function GlossaryContextInitializer({
  alphabetFilter,
  nameFilter,
  onlyFavorites,
  page,
  itemsPerPage,
  termsResponse,
  alphabetResponse,
  children,
}: Props) {
  useAtomInitialValues([
    [
      fetchGlossaryTermsAtom(alphabetFilter, nameFilter, onlyFavorites, page, itemsPerPage),
      AsyncResult.success(termsResponse),
    ],
    [getGlossaryAlphabetAtom, AsyncResult.success(alphabetResponse)],
    // Both filter atoms read the URL, which the server has no access to — their
    // `withServerValue` is `Option.none()`. Left unseeded, every filtered
    // request server-renders the unfiltered branch: the terms fetched for the
    // filter go unused, and the mode flips once hydration reaches the URL.
    [alphabetFilterSearchParamAtom, Option.fromNullishOr(alphabetFilter)],
    [nameFilterSearchParamAtom, Option.fromNullishOr(nameFilter)],
  ]);

  return <>{children}</>;
}
